import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, RadiobuttonItemInfo, Service, SortOrderDirection } from 'webapp-types';

import safeStyleHelper from 'webapp/helpers/safe-style';
import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonSkeletonBarLoaderComponent from 'webapp/components/common/skeleton-bar-loader';
import FormCustomPeriodPickerComponent from 'webapp/components/form/custom-period-picker';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TableSorterComponent from 'webapp/components/table-sorter';

import DateRange from 'webapp/models-custom/date-range';

interface Args {
	// the popup re-centres and re-measures itself when the content height changes
	onContentChange?: () => void;
}

type Period = 'current' | 'previous' | 'custom';

type SortProp = 'name' | 'credits';

type UsageWindow = APIData.Billing.AiCreditsUsageRequest;

interface SourceColumn {
	source: APIData.Billing.AiCreditsSource;
	label: string;
}

interface Row {
	spaceId: number;
	name: string;
	color: string;
	isAgency: boolean;
	credits: number;
	creditsFormatted: string;
	share: string;
	sources: number[]; // in SOURCE_COLUMNS order
	sourcesFormatted: string[];
	other: number;
	otherFormatted: string;
}

const DATE_FORMAT = DateRange.customDateFormat;

const PERIOD_OPTIONS: RadiobuttonItemInfo[] = [
	{ value: 'current', label: 'Current period' },
	{ value: 'previous', label: 'Previous period' },
	{ value: 'custom', label: 'Custom range' },
];

const SOURCE_COLUMNS: SourceColumn[] = [
	{ source: 'chat', label: 'Chat' },
	{ source: 'routine', label: 'Routines' },
	{ source: 'mcp_ask_genie', label: 'MCP' },
	{ source: 'slack', label: 'Slack' },
];

const COLUMN_SOURCES = new Set<string>(SOURCE_COLUMNS.map((column) => column.source));

const CREDITS_FORMAT = new Intl.NumberFormat('en-US', {
	minimumFractionDigits: 1,
	maximumFractionDigits: 1,
});

function formatCredits(credits: number): string {
	return credits ? CREDITS_FORMAT.format(credits) : '–';
}

// `unknown` plus any source the backend added after these columns were defined
function otherCredits(bySource: APIData.Billing.AiCreditsSpaceUsage['bySource']): number {
	return Object.entries(bySource).reduce(
		(sum, [source, credits]) => (COLUMN_SOURCES.has(source) ? sum : sum + (credits ?? 0)),
		0,
	);
}

function csvCell(value: string | number): string {
	let text = String(value);
	// account names are user input: keep spreadsheets from evaluating them as formulas
	if (typeof value === 'string' && /^[=+\-@\t\r]/.test(text)) {
		text = `'${text}`;
	}
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export default class BillingAiCreditsUsageComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked period: Period = 'current';
	@tracked usage: APIData.Billing.AiCreditsUsageResponse | null = null;
	@tracked isLoading = true;
	@tracked hasError = false;
	@tracked customStart: string | null = null;
	@tracked customEnd: string | null = null;
	@tracked sortProp: SortProp = 'credits';
	@tracked sortOrder: SortOrderDirection = 'desc';

	periodOptions = PERIOD_OPTIONS;
	dateFormat = DATE_FORMAT;
	sourceColumns = SOURCE_COLUMNS;

	// the current billing window, as the backend resolved it for a load without a range
	private currentWindow: UsageWindow | null = null;

	private requestCounter = 0;

	private initialLoad: Promise<void>;

	private resizeObserver: ResizeObserver | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.initialLoad = this.load();
	}

	get isCustom(): boolean {
		return this.period === 'custom';
	}

	get rangeText(): string {
		if (!this.usage) {
			return '';
		}
		const from = moment.unix(this.usage.from).utc();
		const lastDay = moment.unix(this.usage.to).utc().subtract(1, 'day');
		return `${from.format('MMM D, YYYY')} – ${lastDay.format('MMM D, YYYY')}`;
	}

	@cached
	get total(): number {
		return (this.usage?.spaces ?? []).reduce((sum, space) => sum + space.credits, 0);
	}

	get totalFormatted(): string {
		return this.total ? formatCredits(this.total) : '0';
	}

	get allowanceFormatted(): string {
		const max = this.billingService.features.aiCreditsMax;
		return max === null ? '∞' : max.toLocaleString('en-US');
	}

	get accountCount(): number {
		return this.usage?.spaces.length ?? 0;
	}

	@cached
	get showOtherColumn(): boolean {
		return this.unsortedRows.some((row) => row.other > 0);
	}

	// built once per response, so sorting doesn't redo the per-row work
	@cached
	get unsortedRows(): Row[] {
		const agencyId = this.mmSpacesService.agencySpaceRecord?.id;
		const total = this.total;

		return (this.usage?.spaces ?? []).map((space): Row => {
			const model = this.mmSpacesService.getBySpaceId(String(space.spaceId));
			const sources = SOURCE_COLUMNS.map((column) => space.bySource[column.source] ?? 0);
			const other = otherCredits(space.bySource);
			return {
				spaceId: space.spaceId,
				name: model?.companyName ?? `Account ${space.spaceId}`,
				color: model?.color ?? '',
				isAgency: String(space.spaceId) === agencyId,
				credits: space.credits,
				creditsFormatted: formatCredits(space.credits),
				share: total ? `${Math.round((space.credits / total) * 100)}%` : '0%',
				sources,
				sourcesFormatted: sources.map(formatCredits),
				other,
				otherFormatted: formatCredits(other),
			};
		});
	}

	@cached
	get rows(): Row[] {
		const direction = this.sortOrder === 'asc' ? 1 : -1;
		return [...this.unsortedRows].sort((a, b) =>
			this.sortProp === 'name'
				? a.name.localeCompare(b.name) * direction
				: (a.credits - b.credits) * direction,
		);
	}

	async load(range?: UsageWindow | 'previous'): Promise<void> {
		const requestId = ++this.requestCounter;
		this.isLoading = true;
		this.hasError = false;

		try {
			const usage = await this.billingService.fetchAiCreditsUsage(
				range === 'previous' ? await this.previousWindow() : range,
			);
			if (!range) {
				// recorded even when superseded, since the previous window is derived from it
				this.currentWindow = { from: usage.from, to: usage.to };
			}
			if (requestId !== this.requestCounter) {
				return; // a newer period was picked meanwhile
			}
			this.usage = usage;
		} catch {
			if (requestId === this.requestCounter) {
				this.hasError = true;
				this.usage = null;
			}
		} finally {
			if (requestId === this.requestCounter) {
				this.isLoading = false;
			}
		}
	}

	private async previousWindow(): Promise<UsageWindow> {
		await this.initialLoad;
		// the first load failed, so ask for the current window again
		if (!this.currentWindow) {
			const usage = await this.billingService.fetchAiCreditsUsage();
			this.currentWindow = { from: usage.from, to: usage.to };
		}
		const to = this.currentWindow.from;
		return { from: moment.unix(to).utc().subtract(1, 'month').unix(), to };
	}

	@action
	selectPeriod(value: AnySimpleValue): void {
		this.changePeriod(value as Period);
	}

	changePeriod(period: Period): void {
		this.period = period;

		if (period === 'current') {
			void this.load();
		} else if (period === 'previous') {
			void this.load('previous');
		} else if (period === 'custom') {
			const from = this.currentWindow?.from ?? moment().utc().startOf('month').unix();
			this.customStart = moment.unix(from).utc().format(DATE_FORMAT);
			this.customEnd = moment().utc().format(DATE_FORMAT);
			this.loadCustom();
		}
	}

	// the picker reports every click (and its own mount), so the range loads only on OK
	@action
	changeCustomPeriod(startDate: string, endDate: string): void {
		this.customStart = startDate;
		this.customEnd = endDate;
	}

	@action
	loadCustom(): void {
		if (!this.customStart || !this.customEnd) {
			return;
		}
		const from = moment.utc(this.customStart, DATE_FORMAT).unix();
		// the picked end day is included; the API's `to` is exclusive
		const to = moment.utc(this.customEnd, DATE_FORMAT).add(1, 'day').unix();
		if (from < to) {
			void this.load({ from, to });
		}
	}

	@action
	changeSortProp(prop: string): void {
		this.sortProp = prop as SortProp;
	}

	@action
	changeSortOrder(order: SortOrderDirection): void {
		this.sortOrder = order;
	}

	// the table renders after the popup has centred itself, so re-centre on every height change
	@action
	observeHeight(element: HTMLElement): void {
		let lastHeight = element.offsetHeight;
		this.resizeObserver = new ResizeObserver(() => {
			if (element.offsetHeight !== lastHeight) {
				lastHeight = element.offsetHeight;
				this.args.onContentChange?.();
			}
		});
		this.resizeObserver.observe(element);
	}

	@action
	stopObservingHeight(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
	}

	@action
	exportCsv(): void {
		if (!this.usage) {
			return;
		}

		const showOther = this.showOtherColumn;
		const header = [
			'Account',
			'Account ID',
			'Credits',
			...SOURCE_COLUMNS.map((column) => column.label),
		];
		if (showOther) {
			header.push('Other');
		}

		const lines = this.rows.map((row) => {
			const cells: (string | number)[] = [row.name, row.spaceId, row.credits, ...row.sources];
			if (showOther) {
				cells.push(row.other);
			}
			return cells.map(csvCell).join(',');
		});

		const from = moment.unix(this.usage.from).utc().format(DATE_FORMAT);
		const to = moment.unix(this.usage.to).utc().subtract(1, 'day').format(DATE_FORMAT);
		// the BOM makes Excel read the file as UTF-8, so non-ASCII account names survive
		const blob = new Blob(['\uFEFF' + [header.join(','), ...lines].join('\n')], {
			type: 'text/csv;charset=utf-8',
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `ai-credits-usage_${from}_${to}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	}

	<template>
		<div
			class="ai-credits-usage"
			{{didInsert this.observeHeight}}
			{{willDestroy this.stopObservingHeight}}
		>
			<p class="ai-credits-usage__intro">
				Credits your agency and each client account used in the selected period.
			</p>

			<div class="ai-credits-usage__period">
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@name="ai-credits-usage-period"
					@selected={{this.period}}
					@options={{this.periodOptions}}
					@onChange={{this.selectPeriod}}
				/>
				{{#if this.isCustom}}
					<FormCustomPeriodPickerComponent
						@format={{this.dateFormat}}
						@startValue={{this.customStart}}
						@endValue={{this.customEnd}}
						@isRolling={{false}}
						@isRollingCheckboxDisabled={{true}}
						@isRollingCheckboxHidden={{true}}
						@onChange={{this.changeCustomPeriod}}
						@onOk={{this.loadCustom}}
						@size="tiny"
					/>
				{{/if}}
				{{#unless this.isCustom}}
					<span class="ai-credits-usage__range">{{this.rangeText}}</span>
				{{/unless}}
			</div>

			{{#if this.isLoading}}
				<div class="ai-credits-usage__loading">
					<CommonSkeletonBarLoaderComponent />
					<CommonSkeletonBarLoaderComponent />
					<CommonSkeletonBarLoaderComponent />
				</div>
			{{else if this.hasError}}
				<p class="ai-credits-usage__empty">
					We couldn't load AI credit usage. Please try again.
				</p>
			{{else}}
				<div class="ai-credits-usage__summary">
					<div>
						<span>Total used</span>
						<strong>{{this.totalFormatted}}</strong>
					</div>
					<div>
						<span>Accounts using AI</span>
						<strong>{{this.accountCount}}</strong>
					</div>
					<div>
						<span>Monthly allowance</span>
						<strong>{{this.allowanceFormatted}}</strong>
					</div>
				</div>

				{{#if this.rows.length}}
					<div class="ai-credits-usage__table-wrapper">
						<table class="ai-credits-usage__table">
							<thead>
								<tr>
									<th>
										<TableSorterComponent
											@prop="name"
											@name="Account"
											@selectedProp={{this.sortProp}}
											@selectedOrder={{this.sortOrder}}
											@changeProp={{this.changeSortProp}}
											@changeOrder={{this.changeSortOrder}}
										/>
									</th>
									<th class="num">
										<TableSorterComponent
											@prop="credits"
											@name="Credits"
											@selectedProp={{this.sortProp}}
											@selectedOrder={{this.sortOrder}}
											@changeProp={{this.changeSortProp}}
											@changeOrder={{this.changeSortOrder}}
										/>
									</th>
									<th class="share">Share</th>
									{{#each this.sourceColumns as |column|}}
										<th class="num">{{column.label}}</th>
									{{/each}}
									{{#if this.showOtherColumn}}
										<th class="num">Other</th>
									{{/if}}
								</tr>
							</thead>
							<tbody>
								{{#each this.rows as |row|}}
									<tr>
										<td>
											<div class="ai-credits-usage__account">
												<AvatarOrInitialsComponent
													@name={{row.name}}
													@color={{row.color}}
												/>
												<span>{{row.name}}</span>
												{{#if row.isAgency}}
													<span class="ai-credits-usage__tag">
														Agency
													</span>
												{{/if}}
											</div>
										</td>
										<td class="num">
											<strong>{{row.creditsFormatted}}</strong>
										</td>
										<td class="share">
											<div class="ai-credits-usage__share">
												<div class="bar">
													<span
														class="bar__value"
														style={{safeStyleHelper
															row.share
															property="width"
														}}
													></span>
												</div>
												<span>{{row.share}}</span>
											</div>
										</td>
										{{#each row.sourcesFormatted as |credits|}}
											<td class="num">{{credits}}</td>
										{{/each}}
										{{#if this.showOtherColumn}}
											<td class="num">{{row.otherFormatted}}</td>
										{{/if}}
									</tr>
								{{/each}}
							</tbody>
						</table>
					</div>
				{{else}}
					<p class="ai-credits-usage__empty">No AI credits were used in this period.</p>
				{{/if}}

				<div class="ai-credits-usage__footer">
					<p>
						Accounts with no AI usage in the period aren't listed. Clients billed
						separately aren't included.
					</p>
					{{#if this.rows.length}}
						<SubmitButtonComponent
							@text="Export CSV"
							@theme="grey-outlined"
							@icon="Download"
							@noDelay={{true}}
							@onTrigger={{this.exportCsv}}
						/>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
